"use client";

import { ReactNode } from "react";

import {
  Block,
  FormatLineSpacing,
  Mail,
  SafetyCheck,
  ScheduleSend,
  VerifiedUser,
} from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";

import { UserActivationConfirmation } from "@/types/openAccount";
import { UserIndicator } from "@/types/user";

import {
  additionalAssistanceClause,
  getGeneralErrorAlert,
  getLookupAlert,
  getRecaptchaAlert,
  getTokenValidationErrorAlert,
  getTokenValidationStatus,
} from "@/components/alerts/global";

function getAccountPreviouslySetupAlert() {
  const passwordResetUrl = `${window.location.origin}/account-utilities/password-reset`;

  return (
    <Alert severity="success" icon={<Mail />}>
      <AlertTitle>Your account has already been opened</AlertTitle>
      <strong>
        You can already use the college systems your account is allowed to
        access.
      </strong>
      <br />
      <br />
      If you do not remember your password you can reset it{" "}
      <a href={passwordResetUrl}>here</a>.{additionalAssistanceClause("ST")}
    </Alert>
  );
}

function getSetupCompletionStatus(timerElement: ReactNode) {
  const content = timerElement ? (
    <strong>You have {timerElement} to complete your account setup.</strong>
  ) : (
    <strong>
      Your account setup has expired. Please restart the open account process.
    </strong>
  );

  return (
    <>
      <br />
      <br />
      {content}
    </>
  );
}

// send email verification success - step 1
function getSendEmailSuccessAlert(
  email: string,
  userIndicator: UserIndicator,
  timerElement: ReactNode
) {
  return (
    <Alert severity="success" icon={<Mail />}>
      <AlertTitle>Account verification email sent</AlertTitle>
      <strong>
        Check your {email} email inbox for an email titled &quot;Your account
        verification&quot;.
      </strong>
      <br />
      <br />
      {getTokenValidationStatus(timerElement)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// expired token - any step
function getExpiredTokenAlert(userIndicator: UserIndicator) {
  return (
    <Alert icon={<Block />} severity="error">
      <AlertTitle>Your account verification has expired</AlertTitle>
      <strong>Account verification must be completed within 15 minutes</strong>
      {restartClause()}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// setup success - step 3
function getSetupSuccessAlert(
  setupAccountData: UserActivationConfirmation,
  userIndicator: UserIndicator
) {
  return (
    <Alert icon={<VerifiedUser />} severity="success">
      <AlertTitle>Congratulations!</AlertTitle>
      You have successfully opened your MPTC account. You can now use the
      college systems your account is allowed to access.
      <br />
      <br />
      Your username is <strong>{setupAccountData.username}</strong>
      <br />
      Your user ID is <strong>{setupAccountData.id}</strong>
      <br />
      Your email is <strong>{setupAccountData.email}</strong>
      <br />
      <br />
      Please write down your username, user ID, and email. You will need them to
      use Moraine Park Technical College systems.
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// accept terms - step 0
function getTermsAlert() {
  return (
    <Alert
      color="info"
      icon={<FormatLineSpacing color="primary" />}
      sx={{ width: "100%" }}
    >
      Please read the procedure and then click &quot;Accept&quot;
    </Alert>
  );
}

// token validation error - step 1

// lookup success - step 1
function getLookupSuccessAlert(userIndicator: UserIndicator, timer: ReactNode) {
  return (
    <Alert severity="warning" icon={<ScheduleSend />}>
      <AlertTitle>Your account information has been found</AlertTitle>
      {getTokenPrevalidationStatus(timer)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

// lookup success - prevalidation - step 1
function getTokenPrevalidationStatus(timer: ReactNode) {
  if (!timer)
    return (
      <strong>
        Your account verification has expired. Please restart the account
        verification process.
      </strong>
    );
  else
    return (
      <strong>
        Select an email below to send your verification link. The link will
        remain valid for {timer}.
      </strong>
    );
}

// email verification status - step 1

// account verification success - step 2
function getVerificationSuccessAlert(
  userIndicator: UserIndicator,
  timerElement: ReactNode
) {
  return (
    <Alert severity="warning" icon={<SafetyCheck />}>
      <AlertTitle>Your account has been verified</AlertTitle>
      <strong>
        Almost there! Just set your password, security settings, and PIN, then
        click &quot;Complete&quot; to finish.
      </strong>
      {getSetupCompletionStatus(timerElement)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

const map = new Map<number, ReactNode>([
  [0, getTermsAlert()],
  [1, getLookupAlert()],
  [2, null],
]);

function restartClause() {
  const restartUrl = `${window.location.origin}/account-utilities/open`;

  return (
    <>
      <br />
      <br />
      You can click <a href={restartUrl}>here</a> to restart at any time.
    </>
  );
}

const openAccountAlerts = {
  getAccountPreviouslySetupAlert,
  getExpiredTokenAlert,
  getGeneralErrorAlert,
  getLookupAlert,
  getLookupSuccessAlert,
  getRecaptchaAlert,
  getSendEmailSuccessAlert,
  getSetupSuccessAlert,
  getTermsAlert,
  getTokenValidationErrorAlert,
  getVerificationSuccessAlert,
  map,
};

export default openAccountAlerts;
